import api from './api';

// API endpoints for wine region management
const API_ENDPOINTS = {
  WINE_REGION: '/admin/wine-region',
  COUNTRIES: '/admin/wine-region/countries',
  ALL_REGIONS: '/admin/wine-region',
  SEARCH: '/admin/wine-region-search/search',
  SUGGESTIONS: '/admin/wine-region-search/suggestions',
};

// Define the WineRegion interface
export interface WineRegion {
  _id: string;
  name: string;
  localName?: string; // Added localName field
  level?: number;
  path?: string;
  parent?: string;
  parentId?: string;
  country?: string;
  hierarchyPath?: string[];
  hierarchyNames?: string[];
  createdAt: string;
  updatedAt: string;
}

// Define pagination metadata interface
export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Define paginated response interface
export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

// Define search suggestion interface
export interface WineRegionSuggestion {
  id: string;
  name: string;
  path: string;
  hierarchyNames: string[];
}

// Define search suggestions response
export interface SearchSuggestionsResponse {
  success: boolean;
  data: {
    suggestions: WineRegionSuggestion[];
  };
  message?: string;
  error?: string;
}

const wineRegionService = {
  /**
   * Get all wine regions
   * @param page Page number (1-based)
   * @param pageSize Number of items per page
   * @param sort Field to sort by
   * @param order Sort order ('asc' or 'desc')
   * @returns Paginated response with all wine regions
   */
  getAllRegions: async (page = 1, pageSize = 20, sort = 'name', order = 'asc'): Promise<PaginatedResponse<WineRegion>> => {
    const response = await api.get(API_ENDPOINTS.ALL_REGIONS, {
      params: { page, pageSize, sort, order }
    });
    return response.data;
  },

  /**
   * Get all countries (top-level regions)
   * @param page Page number (1-based)
   * @param pageSize Number of items per page
   * @param sort Field to sort by
   * @param order Sort order ('asc' or 'desc')
   * @returns Paginated response with countries
   */
  getCountries: async (page = 1, pageSize = 20, sort = 'name', order = 'asc'): Promise<PaginatedResponse<WineRegion>> => {
    const response = await api.get(API_ENDPOINTS.COUNTRIES, {
      params: { page, pageSize, sort, order }
    });
    return response.data;
  },

  /**
   * Get a specific wine region by ID
   */
  getWineRegion: async (id: string): Promise<WineRegion> => {
    const response = await api.get(`${API_ENDPOINTS.WINE_REGION}/${id}`);
    return response.data;
  },

  /**
   * Get children of a specific wine region
   */
  getWineRegionChildren: async (id: string, page = 1, pageSize = 20, sort = 'name', order = 'asc'): Promise<PaginatedResponse<WineRegion>> => {
    const response = await api.get(`${API_ENDPOINTS.WINE_REGION}/${id}/children`, {
      params: { page, pageSize, sort, order }
    });
    return response.data;
  },

  /**
   * Get all descendants of a specific wine region
   */
  getWineRegionDescendants: async (id: string, page = 1, pageSize = 20, sort = 'name', order = 'asc'): Promise<PaginatedResponse<WineRegion>> => {
    const response = await api.get(`${API_ENDPOINTS.WINE_REGION}/${id}/descendants`, {
      params: { page, pageSize, sort, order }
    });
    return response.data;
  },

  /**
   * Create a new wine region
   */
  createWineRegion: async (wineRegion: Omit<WineRegion, '_id' | 'createdAt' | 'updatedAt'>): Promise<WineRegion> => {
    const response = await api.post(API_ENDPOINTS.WINE_REGION, wineRegion);
    return response.data;
  },

  /**
   * Update an existing wine region
   */
  updateWineRegion: async (id: string, wineRegion: Partial<Omit<WineRegion, '_id' | 'createdAt' | 'updatedAt'>>): Promise<WineRegion> => {
    const response = await api.put(`${API_ENDPOINTS.WINE_REGION}/${id}`, wineRegion);
    return response.data;
  },

  /**
   * Delete a wine region
   */
  deleteWineRegion: async (id: string): Promise<void> => {
    await api.delete(`${API_ENDPOINTS.WINE_REGION}/${id}`);
  },

  /**
   * Search wine regions
   * @param query Search query
   * @param page Page number (1-based)
   * @param pageSize Number of items per page
   * @returns Paginated response with matching wine regions
   */
  searchWineRegions: async (query: string, page = 1, pageSize = 20): Promise<PaginatedResponse<WineRegion>> => {
    const response = await api.get(API_ENDPOINTS.SEARCH, {
      params: { q: query, page, pageSize }
    });
    return response.data;
  },

  /**
   * Get wine region search suggestions (autocomplete)
   * @param query Search query
   * @param limit Maximum number of suggestions to return
   * @returns Search suggestions
   */
  getWineRegionSuggestions: async (query: string, limit = 5): Promise<SearchSuggestionsResponse> => {
    const response = await api.get(API_ENDPOINTS.SUGGESTIONS, {
      params: { q: query, limit }
    });
    return response.data;
  }
};

export default wineRegionService;
